import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import PortfolioMetrics from './components/PortfolioMetrics';
import PortfolioPerformanceChart from './components/PortfolioPerformanceChart';
import AssetAllocation from './components/AssetAllocation';
import CurrentHoldings from './components/CurrentHoldings';
import RecentTrades from './components/RecentTrades';
import AIDecisionFeed from './components/AIDecisionFeed';
import WatchlistStatus from './components/WatchlistStatus';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { astraAPI } from '../../services/astraApi';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import ComponentErrorBoundary from '../../components/ComponentErrorBoundary';

/**
 * Trading Dashboard Overview - OPTIMIZED LAYOUT (2025-12-30)
 *
 * Design Principles (Based on TradingView, Robinhood, Bloomberg research):
 * 1. P&L First - The most important metric prominently displayed
 * 2. Speed of Understanding - Instant insights, no cognitive overload
 * 3. Less is More - Removed clutter (SystemStatus, MarketDataFeed, Quick Actions)
 * 4. AI as Differentiator - ASTRA's unique value proposition highlighted
 *
 * Removed Components:
 * - SystemStatus: Technical details not useful for trading decisions
 * - MarketDataFeed: Generic prices available everywhere, not personalized
 * - QuickActionToolbar: Non-functional placeholder buttons
 * - Footer badges: "Secure Connection" etc. = noise
 *
 * Sources:
 * - https://rondesignlab.com/cases/tradingview-platform-for-traders
 * - https://medium.com/@allclonescript/20-best-dashboard-ui-ux-design-principles-you-need-in-2025
 */

const TradingDashboardOverview = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('1D');
  const [refreshing, setRefreshing] = useState(false);
  const [lastAutoRefresh, setLastAutoRefresh] = useState(new Date());
  const [refreshCount, setRefreshCount] = useState(0);
  const refreshKeyRef = useRef(0);

  // 🔧 2025 ENHANCEMENT: Auto-refresh hook for real-time updates
  // Auto-refreshes every 5 seconds without page reload
  const {
    data: sentimentData,
    isLoading: sentimentLoading,
    refresh: refreshSentiment,
    lastUpdate: sentimentLastUpdate,
  } = useAutoRefresh(
    async () => {
      try {
        const tradeHistory = await astraAPI.getTradeHistory(1453, 100);
        const trades = tradeHistory.items || tradeHistory.trades || [];

        if (trades.length === 0) {
          return { bullishPercent: null, description: 'Insufficient trade data for sentiment analysis' };
        }

        let buyCount = 0;
        let sellCount = 0;

        trades.forEach(trade => {
          const action = (trade.side || trade.action || '').toUpperCase();
          if (action === 'BUY') buyCount++;
          else if (action === 'SELL') sellCount++;
        });

        const totalActions = buyCount + sellCount;
        if (totalActions === 0) {
          return { bullishPercent: null, description: 'No trade actions available' };
        }

        const bullishPercent = Math.round((buyCount / totalActions) * 100);

        let description = 'Market sentiment is neutral';
        if (bullishPercent >= 70) description = 'Overall market sentiment is strongly bullish with high buying activity';
        else if (bullishPercent >= 60) description = 'Market sentiment is bullish with moderate buying pressure';
        else if (bullishPercent >= 40) description = 'Market sentiment is neutral with balanced trading activity';
        else if (bullishPercent >= 30) description = 'Market sentiment is bearish with selling pressure';
        else description = 'Market sentiment is strongly bearish with high selling activity';

        return { bullishPercent, description };
      } catch (err) {
        console.error('Failed to calculate market sentiment:', err);
        return { bullishPercent: null, description: 'Unable to calculate market sentiment' };
      }
    },
    {
      interval: 30000, // Auto-refresh every 30 seconds - smoother UX
      enabled: true,
      fetchOnMount: true,
    }
  );

  const marketSentiment = sentimentData || { bullishPercent: null, description: 'Calculating...' };

  // 🔧 PRODUCTION FIX (2025-12-29): Reduced auto-refresh frequency for smoother UX
  // Changed from 15s to 60s to prevent constant re-renders and slow loading
  useEffect(() => {
    const intervalId = setInterval(() => {
      setRefreshCount(prev => prev + 1);
      setLastAutoRefresh(new Date());
    }, 60000); // Refresh every 60 seconds - less aggressive

    return () => clearInterval(intervalId);
  }, []);

  const timeframes = [
    { id: '1D', label: '1 Day' },
    { id: '1W', label: '1 Week' },
    { id: '1M', label: '1 Month' },
    { id: '3M', label: '3 Months' },
    { id: '1Y', label: '1 Year' }
  ];

  // 🔧 2025 FIX: Smooth refresh without page reload
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // Increment refresh key to force child components to re-fetch
      refreshKeyRef.current += 1;
      setRefreshCount(prev => prev + 1);

      // Refresh sentiment data
      await refreshSentiment();

      // Trigger re-render of child components by updating state
      setLastAutoRefresh(new Date());
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      // Small delay to show the refresh animation
      setTimeout(() => setRefreshing(false), 500);
    }
  }, [refreshSentiment]);

  // Note: handleExport and handleFilter removed as part of dashboard optimization
  // Quick Actions toolbar was non-functional - removed to reduce clutter

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'ml-16' : 'ml-64'
      }`}>
        <div className="p-6">
          {/* Page Header - Streamlined */}
          <div className="mb-6">
            <Breadcrumb />
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Portfolio Dashboard</h1>
                <p className="text-muted-foreground mt-1">
                  Real-time P&L monitoring and AI-powered trading insights
                </p>
              </div>

              <div className="flex items-center space-x-3">
                {/* Refresh Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="RefreshCw"
                  iconSize={16}
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className={refreshing ? 'animate-spin' : ''}
                >
                  {refreshing ? '' : 'Refresh'}
                </Button>

                {/* Emergency Stop - Prominent */}
                <Button variant="destructive" size="sm" iconName="AlertOctagon" iconSize={16}>
                  Emergency Stop
                </Button>
              </div>
            </div>
          </div>

          {/* Main Dashboard Grid - OPTIMIZED LAYOUT (2025-12-30) */}
          <div className="space-y-6">

            {/* ROW 1: HERO SECTION - Portfolio Chart + Asset Allocation */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2">
                <ComponentErrorBoundary name="PortfolioPerformanceChart" minHeight="380px">
                  <PortfolioPerformanceChart refreshTrigger={refreshCount} />
                </ComponentErrorBoundary>
              </div>
              <div>
                <ComponentErrorBoundary name="AssetAllocation" minHeight="380px">
                  <AssetAllocation refreshTrigger={refreshCount} />
                </ComponentErrorBoundary>
              </div>
            </div>

            {/* ROW 2: PORTFOLIO METRICS - Condensed INR/USDT breakdown */}
            <div>
              <ComponentErrorBoundary name="PortfolioMetrics" minHeight="120px">
                <PortfolioMetrics refreshTrigger={refreshCount} />
              </ComponentErrorBoundary>
            </div>

            {/* ROW 3: HOLDINGS + AI DECISIONS + WATCHLIST (Three-column layout) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              <div>
                <ComponentErrorBoundary name="CurrentHoldings" minHeight="400px">
                  <CurrentHoldings refreshTrigger={refreshCount} />
                </ComponentErrorBoundary>
              </div>
              <div>
                <ComponentErrorBoundary name="AIDecisionFeed" minHeight="400px">
                  <AIDecisionFeed refreshTrigger={refreshCount} />
                </ComponentErrorBoundary>
              </div>
              <div className="lg:col-span-2 xl:col-span-1">
                <ComponentErrorBoundary name="WatchlistStatus" minHeight="300px">
                  <WatchlistStatus refreshTrigger={refreshCount} />
                </ComponentErrorBoundary>
              </div>
            </div>

            {/* ROW 4: RECENT TRADES - Full width activity log */}
            <div>
              <ComponentErrorBoundary name="RecentTrades" minHeight="250px">
                <RecentTrades refreshTrigger={refreshCount} />
              </ComponentErrorBoundary>
            </div>

            {/* Market Sentiment Banner - Compact inline display */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-lg p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Activity" size={18} className="text-primary" />
                    <span className="font-medium text-foreground">Market Sentiment</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {marketSentiment.description}
                  </span>
                </div>

                {marketSentiment.bullishPercent !== null && (
                  <div className="flex items-center space-x-3">
                    <div className="w-32 bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          marketSentiment.bullishPercent >= 60 ? 'bg-success' :
                          marketSentiment.bullishPercent >= 40 ? 'bg-warning' : 'bg-error'
                        }`}
                        style={{ width: `${marketSentiment.bullishPercent}%` }}
                      ></div>
                    </div>
                    <span className={`text-sm font-bold ${
                      marketSentiment.bullishPercent >= 60 ? 'text-success' :
                      marketSentiment.bullishPercent >= 40 ? 'text-warning' : 'text-error'
                    }`}>
                      {marketSentiment.bullishPercent}%
                      {marketSentiment.bullishPercent >= 60 ? ' Bullish' :
                       marketSentiment.bullishPercent >= 40 ? ' Neutral' : ' Bearish'}
                    </span>
                  </div>
                )}

                <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                  <Icon name="Clock" size={12} />
                  <span>Updated: {lastAutoRefresh?.toLocaleTimeString('en-IN')}</span>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default TradingDashboardOverview;